import { computed, Injector } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { DataConnect } from '@angular/fire/data-connect';
import { Observable } from 'rxjs';
import { subscribe, QueryResult, DataConnectError } from 'firebase/data-connect';

/**
 * A generated Data Connect ref function, callable with or without an explicit
 * `DataConnect` instance (matching the shape Firebase's codegen produces).
 */
export interface DataConnectQueryRefFactory<TVariables> {
  (vars: TVariables): any;
  (dc: DataConnect, vars: TVariables): any;
}

const RECONNECT_RETRY_DELAYS_MS = [500, 1500, 4500];

// The Data Connect SDK gives up permanently (no further internal retries) when a reconnect
// attempt races a tab being hidden/closed and its IndexedDB connection is mid-teardown. Left
// alone this kills the live subscription and puts the resource into a permanent error state.
function isTransientReconnectTermination(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '');
  return message.includes('reconnect attempt') && message.includes('closing/hidden');
}

/**
 * A reusable wrapper to stream Firebase Data Connect queries into Angular v22 Signals.
 * * @param refFactory The generated Firebase Data Connect ref function (e.g., getMovieByIdRef)
 * @param paramsFn A function returning the reactive variables for the query. Return `undefined`
 * when the query isn't ready to run yet (e.g. no id selected) — matching the standard Angular
 * resource() convention, this skips running the query entirely instead of calling it with a
 * placeholder value that the backend would reject (e.g. an empty-string UUID).
 * @param injector Optional injector to resolve a non-default `DataConnect` instance from
 * (e.g. a connector configured via a child injector like `PUBLIC_DATA_CONNECT_INJECTOR`)
 */
/**
 * Data Connect surfaces `@auth` rejections as a plain GraphQL error message with no
 * structured error code (DataConnectOperationError.code is always the generic
 * 'partial-error', regardless of cause), so substring matching on the message is the
 * only signal available client-side.
 */
export function getDataConnectDeleteErrorMessage(error: unknown, entityName: string): string {
  const message = error instanceof Error ? error.message : '';
  if (message.includes('PERMISSION_DENIED') || message.includes('Unauthorized') || message.includes('admin')) {
    return `You do not have permission to delete ${entityName}. Admin access required.`;
  }
  return `Error deleting ${entityName}. Please try again.`;
}

export function streamDataConnectQuery<TData, TVariables>(
  refFactory: DataConnectQueryRefFactory<TVariables>,
  paramsFn: () => TVariables | undefined,
  injector?: Injector
) {
  // Callers typically build a fresh variables object/array on every recompute, which would restart
  // the stream even when the values are unchanged. Re-subscribing the same query+variables while
  // its initial fetch is in flight hits a queued-request path in @firebase/data-connect (0.7.4)
  // that drops its promise (`void executeOrResumeQuery(...)`), so any error on that fetch becomes
  // an unhandled rejection on top of the one already delivered to onError. Data Connect variables
  // are plain JSON, so structural comparison is safe and only restarts on a real change.
  const params = computed(paramsFn, {
    equal: (a, b) => JSON.stringify(a) === JSON.stringify(b),
  });

  return rxResource({
    // 1. Reactively track the variables passed in from the component
    params,

    // 2. Wrap the stream lifecycle
    stream: ({ params }) => {
      return new Observable<TData>((subscriber) => {
        let stopped = false;
        let unsubscribeCurrent: () => void;
        let reconnectAttempt = 0;

        const startSubscription = () => {
          // Generate the query ref using the current parameters, resolving the
          // DataConnect instance from the given injector when one is provided
          const queryRef = injector ? refFactory(injector.get(DataConnect), params) : refFactory(params);

          const onNext = (result: QueryResult<TData, TVariables>) => {
            reconnectAttempt = 0;
            subscriber.next(result.data);
          };

          const onError = (err?: DataConnectError) => {
            if (stopped) return;

            if (isTransientReconnectTermination(err) && reconnectAttempt < RECONNECT_RETRY_DELAYS_MS.length) {
              const delay = RECONNECT_RETRY_DELAYS_MS[reconnectAttempt++];
              setTimeout(() => {
                if (!stopped) startSubscription();
              }, delay);
              return;
            }

            subscriber.error(err instanceof Error ? err : new Error('Data Connect query stream failed with no error object', { cause: err }));
          };

          const onComplete = () => {
            subscriber.complete();
          };

          // Start the continuous subscription
          unsubscribeCurrent = subscribe(queryRef, onNext, onError, onComplete);
        };

        startSubscription();

        // Cleanup: Angular calls this automatically when params change or the component dies
        return () => {
          stopped = true;
          unsubscribeCurrent();
        };
      });
    },
  });
}